import { useMemo, useState } from 'react'

import {
  Box,
  Checkbox,
  Icon,
  Text,
  Tooltip,
  useBreakpoint,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { ChevronLeft } from '@factorialco/f0-react/icons/app'
import {
  CreateSelfUpsellDocument,
  EstimateProrateDocument,
  NextInvoiceDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import LeftSection from 'modules/upsell/components/SelfUpsell/CheckoutModal/LeftSection'
import PaymentDetail from 'modules/upsell/components/SelfUpsell/CheckoutModal/PaymentDetail'
import useSelfUpsellPrices from 'modules/upsell/hooks/useSelfUpsellPrices'
import { LEGAL_LINKS } from 'modules/upsell/lib/legalLinks'
import type { ApiDiscount } from 'modules/upsell/lib/selfUpsell/discountTypes'
import { DEFAULT_DISCOUNT_PERIOD } from 'modules/upsell/lib/selfUpsell/discountTypes'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { formatDate } from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

type TermsAndConditionsProps = {
  hasAcceptedTerms: boolean
  setHasAcceptedTerms: (hasAcceptedTerms: boolean) => void
  locale: string
  checkboxError: boolean
  setCheckboxError: (checkboxError: boolean) => void
}

const TermsAndConditions = ({
  hasAcceptedTerms,
  setHasAcceptedTerms,
  locale,
  checkboxError,
  setCheckboxError,
}: TermsAndConditionsProps) => {
  const getLegalLinks = (): { terms: string; privacy: string } => {
    if (LEGAL_LINKS[locale]) return LEGAL_LINKS[locale]
    const baseLocale = locale.split('-')[0] || 'en'

    return (
      LEGAL_LINKS[baseLocale] || {
        terms: 'https://factorialhr.com/terms-of-use',
        privacy: 'https://factorialhr.com/privacy',
      }
    )
  }

  const { terms, privacy } = getLegalLinks()

  const handleCheckboxChange = () => {
    setHasAcceptedTerms(!hasAcceptedTerms)
    setCheckboxError(false)
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="flexStart">
      <Checkbox checked={hasAcceptedTerms} onChange={handleCheckboxChange} size="large" />
      <Box alignSelf="start">
        <Box gap="s4">
          <Text.Normal weight="normal" color="grey800" size="200">
            {i18n.t('self_upsell.second_step.terms_checkbox_1')}{' '}
            <span
              onClick={() => {
                window.open(terms, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_2')}
              </Text.Normal>
            </span>{' '}
            {i18n.t('self_upsell.second_step.terms_checkbox_3')}{' '}
            <span
              onClick={() => {
                window.open(privacy, '_blank')
              }}
            >
              <Text.Normal weight="medium" color="grey1000" size="200" underlined>
                {i18n.t('self_upsell.second_step.terms_checkbox_4')}
              </Text.Normal>
            </span>
            .
          </Text.Normal>
          {checkboxError && (
            <Text.Normal color="moodSuperNegative" size="100">
              {i18n.t('self_upsell.second_step.terms_checkbox_error')}
            </Text.Normal>
          )}
        </Box>
      </Box>
    </Box>
  )
}
type SecondStepProps = {
  addon: Plan
  crossSellingAddons?: Plan[] | undefined
  mandatoryCrossSellingAddons?: Plan[] | undefined
  step: string
  setStep: (step: string) => void
  moduleName: string
}

const SecondStep = ({
  addon,
  crossSellingAddons,
  mandatoryCrossSellingAddons,
  step,
  setStep,
  moduleName,
}: SecondStepProps) => {
  const { company } = useCurrentSession()
  const [open, toggle] = useToggle(false)
  const { isMobile } = useBreakpoint()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [checkboxError, setCheckboxError] = useState(false)
  const [selectedCrossSellingAddons, setSelectedCrossSellingAddons] = useState<Plan[]>([])
  const [proratedPrice, setProratedPrice] = useState<{
    amount: number
    currency: string
    discounts: ApiDiscount[]
  } | null>(null)
  const { configPlan, period, currency, seats, shouldBypassSelfUpsell, currentSeats, isExpenses } =
    useSelfUpsellContext()
  const { perSeatPrice, feePrice, stairstepPrice, totalPriceAfterDiscount, discountedAddons } =
    useSelfUpsellPrices({
      addon,
      discounts: proratedPrice?.discounts,
      crossSellingAddons,
      mandatoryCrossSellingAddons,
    })
  const [fetchNextInvoiceData, { data: nextInvoiceData }] = useLazyQuery(NextInvoiceDocument)

  // Create a stable string representation of selected cross-selling addons for dependency array
  const selectedCrossSellingAddonsKey = useMemo(
    () => selectedCrossSellingAddons.map((plan) => plan.pricing.getPlanId(period) || '').join(','),
    [selectedCrossSellingAddons, period]
  )

  // Create a stable string representation of configPlan.addon for dependency array
  const configPlanAddonKey = useMemo(
    () =>
      (Array.isArray(configPlan.addon) ? configPlan.addon : [configPlan.addon])
        .filter(Boolean)
        .join(','),
    [configPlan.addon]
  )
  const nextInvoiceDate =
    nextInvoiceData?.billing.cpNextBillingEstimatesConnection.nodes[0]?.subscriptionEstimate
      ?.nextBillingAt

  const today = new Date()
  const formattedToday = formatDate(today)
  const billingDate = nextInvoiceDate ? new Date(nextInvoiceDate * 1000) : new Date()
  const formattedBillingDate = formatDate(billingDate)
  const tooltipLabel = `${i18n.t(
    'self_upsell.second_step.tooltip'
  )} ${formattedToday} - ${formattedBillingDate}. ${i18n.t(
    'self_upsell.second_step.prorated_description'
  )}`

  const [estimateProrate, { loading }] = useMutation(EstimateProrateDocument, {
    extractorFn: (data) => data.billingMutations.createBillingEstimateProrate,
  })

  const [selfUpsellSubscription] = useMutation(CreateSelfUpsellDocument, {
    extractorFn: (data) => data.billingMutations.createBillingUpgradeSubscription,
  })

  const getEstimateProrate = async () => {
    if (!configPlan.addon) return

    const result = await estimateProrate({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem,
              seats: isExpenses() ? seats : currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon,
                seats: isExpenses() ? seats : currentSeats,
                module: moduleName,
              },
            ],
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      return {
        amount: handled.data?.estimateProrate?.amount,
        currency: handled.data?.estimateProrate?.currency,
        discounts: handled.data?.estimateProrate?.discounts,
      }
    }
  }

  // Helper function to get discounted price for an addon
  const getDiscountedPriceForAddon = (addonItem: Plan): number => {
    const planId = addonItem.pricing.getPlanId(period) || ''
    const discountedAddon = discountedAddons.get(planId)

    if (discountedAddon) {
      return discountedAddon.discountedPrice
    }

    // If no discount applies, return the original total price
    return addonItem.pricing.getTotalPrice(period, isExpenses() ? seats : currentSeats) || 0
  }

  const trackEvent = () => {
    track(trackingEvents.upsell.clickConfirmPaymentButton, {
      module: moduleName,
      selectedPlan: configPlan.variant,
      seats: seats,
      billingPeriod: period,
      upsellAmount: totalPriceAfterDiscount,
      proratedAmount: proratedPrice?.amount,
      upsellOrigin: readLatestUpsellOrigin()?.request_source,
    })
  }

  const handleConfirm = async () => {
    setCheckboxError(false)
    if (!hasAcceptedTerms) setCheckboxError(true)
    if (!configPlan.addon || !hasAcceptedTerms || !configPlan.variant) return
    if (shouldBypassSelfUpsell) {
      setStep('success')
      return
    }

    trackEvent()

    const crossSellingInfo = () => {
      const allCrossSelling = [
        ...(mandatoryCrossSellingAddons?.filter(
          (addon) => configPlan.addon?.includes(addon.pricing.getPlanId(period) || '') || false
        ) || []),
        ...(crossSellingAddons?.filter(
          (addon) => configPlan.addon?.includes(addon.pricing.getPlanId(period) || '') || false
        ) || []),
      ]
      if (allCrossSelling.length > 0)
        return allCrossSelling.map((crossSellingAddonItem) => {
          const billedSeats = isExpenses() ? seats : currentSeats
          return {
            lineItemId: crossSellingAddonItem.pricing.getPlanId(period) || '',
            itemName: `${crossSellingAddonItem?.title}`,
            moduleRequested: crossSellingAddonItem.internalTitle,
            pricePerSeat: crossSellingAddonItem.pricing.getSeatsPrice(period) || 0,
            priceFixed: crossSellingAddonItem.pricing.getFeePrice(period) || 0,
            priceStairstep:
              crossSellingAddonItem.pricing.getStairstepPrice(period, billedSeats) || 0,
            proratedPrice: 0,
            seats: billedSeats,
            currency: currency,
            totalPrice: getDiscountedPriceForAddon(crossSellingAddonItem),
            billingDate: formattedBillingDate,
            billingPeriod: period,
          }
        })
    }
    const latestUpsellOrigin = readLatestUpsellOrigin()
    const upsellOrigin = latestUpsellOrigin?.request_source
    const upsellOriginCategory = latestUpsellOrigin?.request_source_category || 'Organic'
    const upsellOriginComponent = latestUpsellOrigin?.request_source_component
    const selfUpsellRequestSource = upsellOrigin || 'Marketplace'

    const result = await selfUpsellSubscription({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem,
              seats: isExpenses() ? seats : currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon,
                seats: isExpenses() ? seats : currentSeats,
                module: moduleName,
              },
            ],
        origin: 'Self upsell - Marketplace detail page',
        upsellInfo: {
          lineItemId: addon.pricing.getPlanId(period) || '',
          itemName: `${addon.title} ${configPlan.variant}`,
          moduleRequested: moduleName,
          pricePerSeat: perSeatPrice === 0 ? undefined : perSeatPrice,
          priceFixed: feePrice === 0 ? undefined : feePrice,
          priceStairstep: stairstepPrice === 0 ? undefined : stairstepPrice,
          proratedPrice: proratedPrice?.amount ? Number(proratedPrice.amount) : undefined,
          seats: isExpenses() ? seats : currentSeats,
          currency: currency,
          totalPrice: getDiscountedPriceForAddon(addon),
          billingDate: formattedBillingDate,
          billingPeriod: period,
          requestSource: selfUpsellRequestSource,
          requestSourceCategory: upsellOriginCategory,
          requestSourceComponent: upsellOriginComponent,
          upsellRequestType: 'Self-upsell',
        },
        crossSellingInfo: crossSellingInfo() ?? [],
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      setStep('success')
    } else {
      setStep('error')
    }
  }

  useEffect(() => {
    if (shouldBypassSelfUpsell) return
    getEstimateProrate().then((data) => {
      if (!data) {
        setProratedPrice((prev) => prev ?? { amount: 0, currency, discounts: [] })
        return
      }
      setProratedPrice({
        amount: data.amount ?? 0,
        currency: data.currency ?? currency,
        discounts:
          data.discounts
            ?.filter((d) => d.discountPercentage && d.amount)
            .map(
              (d): ApiDiscount => ({
                discountPercentage: d.discountPercentage ?? 0,
                amount: d.amount ?? 0,
                discountType: d.discountType ?? '',
                lineItemId: d.lineItemId ?? '',
                period: d.period ?? DEFAULT_DISCOUNT_PERIOD.VALUE,
                periodUnit: d.periodUnit ?? DEFAULT_DISCOUNT_PERIOD.UNIT,
              })
            ) ?? [],
      })
    })
    fetchNextInvoiceData({
      variables: { companyId: company.id },
    })
  }, [seats, currency, shouldBypassSelfUpsell, selectedCrossSellingAddonsKey, configPlanAddonKey])

  const isLoadingDiscounts = !shouldBypassSelfUpsell && (loading || !proratedPrice)

  return (
    <Box flexDirection="row" height="full">
      <LeftSection
        addon={addon}
        variant={configPlan.variant}
        step={step}
        iconName={moduleName}
        crossSellingAddons={crossSellingAddons}
        mandatoryCrossSellingAddons={mandatoryCrossSellingAddons}
        selectedCrossSellingAddons={selectedCrossSellingAddons}
        setSelectedCrossSellingAddons={setSelectedCrossSellingAddons}
        discounts={proratedPrice?.discounts}
        isLoadingDiscounts={isLoadingDiscounts}
      />

      <Box
        padding="s24"
        justifyContent="spaceBetween"
        style={{
          width: isMobile ? '100%' : '60%',
        }}
      >
        <Box gap="s16">
          <Box height="s24" />
          {isMobile && (
            <Text size="400" color="grey1000" weight="semibold">
              {addon.title}
            </Text>
          )}
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('self_upsell.second_step.order_summary')}
          </Text.Normal>
          <PaymentDetail
            addon={addon}
            crossSellingAddons={crossSellingAddons}
            mandatoryCrossSellingAddons={mandatoryCrossSellingAddons}
            billingDate={formattedBillingDate}
            discounts={proratedPrice?.discounts}
            loadingPrice={loading}
          />
          <TermsAndConditions
            hasAcceptedTerms={hasAcceptedTerms}
            setHasAcceptedTerms={setHasAcceptedTerms}
            locale={company.locale || 'en'}
            checkboxError={checkboxError}
            setCheckboxError={setCheckboxError}
          />
        </Box>
        <Box
          gap="s16"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          flexWrap="wrap"
          style={{
            height: '56px',
          }}
        >
          <Box gap="s4">
            <Box flexDirection="row" alignItems="center" gap="s4">
              <Text.Normal size="200" color="grey700" weight="medium">
                {i18n.t('self_upsell.common.amount_today')}
              </Text.Normal>
              <Box alignSelf="center">
                <Tooltip.Alt
                  size="small"
                  attachment="bottom center"
                  open={open}
                  toggle={toggle}
                  label={tooltipLabel}
                >
                  <Icon.Medium icon="info-circle-line" />
                </Tooltip.Alt>
              </Box>
            </Box>
            <Text size="400" color="grey1000" weight="semibold">
              {proratedPrice ? formatPrice(proratedPrice.amount, proratedPrice.currency) : '-'}
            </Text>
          </Box>
          <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
            <F0Button
              hideLabel
              icon={ChevronLeft}
              variant="outline"
              round
              size="lg"
              onClick={() => setStep('first')}
            />
            <F0Button
              size="lg"
              label={i18n.t('self_upsell.second_step.button')}
              onClick={handleConfirm}
              disabled={!configPlan.addon}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SecondStep
